import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { readConsumerRuntimeDeviceMode, readConsumerRuntimeTableCode } from "../runtime-device-mode";
import LanguageSelector from "../components/LanguageSelector";
import { trn, useI18n } from "../shared/i18n";
import { readTerminalConfig } from "../terminal-theme";

const IdleScreen = () => {
  const navigate = useNavigate();
  const { language } = useI18n();
  void language;
  const deviceMode = readConsumerRuntimeDeviceMode();
  const tableCode = readConsumerRuntimeTableCode();
  const terminalConfig = readTerminalConfig();
  const readBackgroundImage = () =>
    String((terminalConfig as { kioskBackgroundImage?: string } | null)?.kioskBackgroundImage || "").trim() ||
    localStorage.getItem("marketplaceBackgroundImage") ||
    "";
  const [backgroundImage, setBackgroundImage] = useState(readBackgroundImage);
  const idleConfig = terminalConfig?.consumerRuntime?.idleScreen || null;
  const overlayOpacity = Math.max(0, Math.min(90, Number(idleConfig?.overlayOpacity ?? 36)));
  const logoUrl = String(idleConfig?.logoUrl || "").trim();
  const logoWidth = Math.max(80, Math.min(720, Number(idleConfig?.logoWidth ?? 240)));
  const logoPosition = String(idleConfig?.logoPosition || "top-center").trim();

  useEffect(() => {
    const refreshBackground = () => setBackgroundImage(readBackgroundImage());
    window.addEventListener("runtime-branding:updated", refreshBackground);
    return () => window.removeEventListener("runtime-branding:updated", refreshBackground);
  }, []);

  const handleStart = () => {
    if (deviceMode === "table") {
      navigate(`/menu?type=eat-in${tableCode ? `&table=${encodeURIComponent(tableCode)}` : ""}`);
      return;
    }
    navigate("/order-type");
  };

  return (
    <div
      className="idle-screen"
      onClick={handleStart}
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#000", // 🔥 Siempre fondo negro antes de cargar imagen
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      {/* 🔹 Fondo con imagen solo cuando esté cargada */}
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "absolute",
          top: 0,
          left: 0,
          transition: "opacity 0.5s ease-in-out",
        }}
      ></div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `rgba(0,0,0,${overlayOpacity / 100})`,
          zIndex: 1,
        }}
      />

      {logoUrl ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            padding: "2.25rem",
            pointerEvents: "none",
            ...resolveIdleLogoPositionStyle(logoPosition),
          }}
        >
          <img
            src={logoUrl}
            alt={trn("app.consumer.idle.logo_alt", "Restaurant logo")}
            style={{
              width: `${logoWidth}px`,
              maxWidth: "42vw",
              maxHeight: "22vh",
              objectFit: "contain",
              filter: "drop-shadow(0 18px 36px rgba(0,0,0,0.45))",
            }}
          />
        </div>
      ) : null}

      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "absolute",
          top: "1.5rem",
          right: "1.5rem",
          zIndex: 4,
          display: "flex",
          justifyContent: "flex-end",
          maxWidth: "calc(100vw - 3rem)",
        }}
      >
        <LanguageSelector compact dark />
      </div>

      {/* 🔹 Texto CENTRADO y sin saltos de línea */}
      <div
        style={{
          position: "absolute",
          color: "white",
          fontSize: "3rem", // 🔥 Ajustado para una sola línea
          background: "rgba(0,0,0,0.6)",
          padding: "1rem 2.5rem", // 🔥 Ajustado para evitar saltos
          borderRadius: "10px",
          textAlign: "center",
          cursor: "pointer", // ✅ Hace que el área sea clickeable
          whiteSpace: "nowrap", // 🔥 ¡EVITA el salto de línea!
          zIndex: 3, // 🔥 Se asegura de que esté por encima de la imagen
        }}
        >
        {deviceMode === "table"
          ? trn(
              "app.consumer.idle.tap_to_order_table",
              "Tap the screen to order · Table {tableCode}",
              { tableCode: tableCode || "?" }
            )
          : trn("app.consumer.idle.tap_to_start", "Tap the screen to start")}
      </div>
    </div>
  );
};

function resolveIdleLogoPositionStyle(position: string): {
  justifyContent: "flex-start" | "center" | "flex-end";
  alignItems: "flex-start" | "center" | "flex-end";
} {
  switch (position) {
    case "top-left":
      return { justifyContent: "flex-start", alignItems: "flex-start" };
    case "top-right":
      return { justifyContent: "flex-end", alignItems: "flex-start" };
    case "center-left":
      return { justifyContent: "flex-start", alignItems: "center" };
    case "center":
      return { justifyContent: "center", alignItems: "center" };
    case "center-right":
      return { justifyContent: "flex-end", alignItems: "center" };
    case "bottom-left":
      return { justifyContent: "flex-start", alignItems: "flex-end" };
    case "bottom-center":
      return { justifyContent: "center", alignItems: "flex-end" };
    case "bottom-right":
      return { justifyContent: "flex-end", alignItems: "flex-end" };
    case "top-center":
    default:
      return { justifyContent: "center", alignItems: "flex-start" };
  }
}

export default IdleScreen;
